import React, { useEffect } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { Offer } from 'common/contracts';
import { EPopupSubType } from 'constants/enums';

import { PopupsFormFields } from '../../PopupsForm.consts';
import { ProductEntry } from '../usePopupProducts';

import { prepareInitialFormValues } from './usePopupFormInit.utils';

interface IProgressBarFormValues {
  progressBarId: string | null;
  progressBarPoints: number | null;
}

interface IUsePopupFormInitProps {
  form: UseFormReturn<PopupsFormFields>;
  edit: boolean;
  dup: boolean;
  popupId: string | undefined;
  defaultOfferUiId: string;
  currentOffer: Offer | undefined;
  setMultiProducts: (products: ProductEntry[]) => void;
  setSingleProducts: (products: ProductEntry[]) => void;
  setProgressBarFormValues: React.Dispatch<React.SetStateAction<IProgressBarFormValues>>;
}

/**
 * Handles form initialization for edit/duplicate/create flows of the Popup form.
 *
 * @example
 * usePopupFormInit({ form, edit, dup, popupId, defaultOfferUiId, currentOffer, setMultiProducts, setSingleProducts, setProgressBarFormValues });
 */
export const usePopupFormInit = ({
  form,
  edit,
  dup,
  popupId,
  defaultOfferUiId,
  currentOffer,
  setMultiProducts,
  setSingleProducts,
  setProgressBarFormValues
}: IUsePopupFormInitProps): void => {
  useEffect(() => {
    if (!popupId && defaultOfferUiId) {
      form.setValue('offerUiId', defaultOfferUiId, {
        shouldDirty: false,
        shouldValidate: true
      });
    }

    if ((edit || dup) && currentOffer) {
      const { products, formValues } = prepareInitialFormValues(
        currentOffer,
        dup,
        edit,
        defaultOfferUiId
      );
      if (formValues.subType === EPopupSubType.POST_PURCHASE) {
        setSingleProducts(products);
      } else {
        setMultiProducts(products);
      }
      setProgressBarFormValues({
        progressBarPoints:
          currentOffer.productsSequence?.[0]?.progressBarPoints?.[0]?.points ?? null,
        progressBarId:
          currentOffer.productsSequence?.[0]?.progressBarPoints?.[0]?.barId ?? null
      });
      form.reset(formValues);
    }
  }, [
    edit,
    dup,
    currentOffer,
    form,
    popupId,
    defaultOfferUiId,
    setMultiProducts,
    setSingleProducts,
    setProgressBarFormValues
  ]);
};
